import React, { useCallback, useEffect, useState } from 'react';
import { getPhoneErrorReports } from '../api/database';

type Props = {
  userEmail?: string;
  onLogout: () => void;
};

type DetailField = {
  label: string;
  value: string;
  multiline?: boolean;
};

type PhoneErrorReport = Awaited<ReturnType<typeof getPhoneErrorReports>>['errors'][number];

const PAGE_SIZE = 50;

type AppliedFilters = {
  email: string;
  start: string;
};

function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

/** Convert datetime-local value (YYYY-MM-DDTHH:mm) to an ISO string for the API. */
function datetimeLocalToIso(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

function displayValue(value: string | null | undefined): string {
  const text = String(value ?? '').trim();
  return text || '—';
}

function buildDetailFields(row: PhoneErrorReport): DetailField[] {
  const patientName = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();

  return [
    { label: 'ErrorID', value: displayValue(row.id) },
    { label: 'Email', value: displayValue(row.email) },
    { label: 'Patient name', value: displayValue(patientName) },
    { label: 'Patient ID', value: displayValue(row.pat_id) },
    { label: 'Timestamp', value: formatTimestamp(row.timestamp) },
    { label: 'Page', value: displayValue(row.page) },
    { label: 'Operation', value: displayValue(row.operation) },
    { label: 'Apps version', value: displayValue(row.apps_version) },
    { label: 'Python processing version', value: displayValue(row.python_processing_version) },
    { label: 'Firmware version', value: displayValue(row.firmware_version) },
    { label: 'Device ID', value: displayValue(row.device_id) },
    { label: 'Phone', value: displayValue(row.phone) },
    { label: 'Network', value: displayValue(row.network) },
    { label: 'Error', value: displayValue(row.error), multiline: true },
  ];
}

const AdminPhoneErrorsPage: React.FC<Props> = ({ userEmail, onLogout }) => {
  const [errors, setErrors] = useState<PhoneErrorReport[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [emailFilter, setEmailFilter] = useState('');
  const [startFilter, setStartFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({ email: '', start: '' });

  const loadErrors = useCallback(async (nextOffset: number, append: boolean, filters: AppliedFilters) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setFetchError(null);

    try {
      const response = await getPhoneErrorReports({
        limit: PAGE_SIZE,
        offset: nextOffset,
        email: filters.email || undefined,
        start: datetimeLocalToIso(filters.start),
      });
      setTotal(response.total);
      setOffset(nextOffset);
      setErrors((prev) => (append ? [...prev, ...response.errors] : response.errors));
    } catch (error) {
      setFetchError(error instanceof Error ? error.message : 'Failed to load phone errors');
      if (!append) {
        setErrors([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadErrors(0, false, appliedFilters);
  }, [appliedFilters, loadErrors]);

  const handleApplyFilter = (event: React.FormEvent) => {
    event.preventDefault();
    setExpandedId(null);
    setAppliedFilters({
      email: emailFilter.trim(),
      start: startFilter.trim(),
    });
  };

  const handleClearFilter = () => {
    setEmailFilter('');
    setStartFilter('');
    setAppliedFilters({ email: '', start: '' });
    setExpandedId(null);
  };

  const handleRefresh = () => {
    setExpandedId(null);
    loadErrors(0, false, appliedFilters);
  };

  const hasActiveFilters = Boolean(appliedFilters.email || appliedFilters.start);

  const toggleExpanded = (rowId: string) => {
    setExpandedId((current) => (current === rowId ? null : rowId));
  };

  const hasMore = errors.length < total;

  return (
    <div className="admin-errors-page">
      <header className="admin-errors-header">
        <div>
          <h1>Phone Error Reports</h1>
          <p className="admin-errors-subtitle">
            Admin view for mobile app crashes and failures
            {userEmail ? ` · ${userEmail}` : ''}
          </p>
        </div>
        <button type="button" className="btn btn-login admin-errors-logout" onClick={onLogout}>
          Log out
        </button>
      </header>

      <section className="admin-errors-toolbar">
        <form className="admin-errors-filter" onSubmit={handleApplyFilter}>
          <input
            type="email"
            value={emailFilter}
            onChange={(event) => setEmailFilter(event.target.value)}
            placeholder="Filter by patient email"
            aria-label="Filter by patient email"
          />
          <label className="admin-errors-filter-field">
            <span>From</span>
            <input
              type="datetime-local"
              value={startFilter}
              onChange={(event) => setStartFilter(event.target.value)}
              aria-label="Show errors from this timestamp onward"
            />
          </label>
          <button type="submit" className="btn btn-login">
            Apply
          </button>
          <button
            type="button"
            className="btn admin-errors-refresh"
            disabled={loading}
            onClick={handleRefresh}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          {hasActiveFilters && (
            <button type="button" className="btn admin-errors-clear" onClick={handleClearFilter}>
              Clear
            </button>
          )}
        </form>
        <div className="admin-errors-count">
          {loading ? 'Loading…' : `${total} report${total === 1 ? '' : 's'}`}
        </div>
      </section>

      {fetchError && <div className="admin-errors-alert">{fetchError}</div>}

      {!loading && errors.length === 0 && !fetchError && (
        <div className="admin-errors-empty">No phone error reports found.</div>
      )}

      {errors.length > 0 && (
        <div className="admin-errors-table-wrap">
          <table className="admin-errors-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Patient ID</th>
                <th>Page</th>
                <th>Operation</th>
                <th>Error</th>
                <th aria-label="Expand details" />
              </tr>
            </thead>
            <tbody>
              {errors.map((row) => {
                const rowId = String(row.id);
                const isExpanded = expandedId === rowId;
                const detailFields = buildDetailFields(row);

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`admin-errors-row${isExpanded ? ' admin-errors-row-expanded' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                      onClick={() => toggleExpanded(rowId)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleExpanded(rowId);
                        }
                      }}
                    >
                      <td>{formatTimestamp(row.timestamp)}</td>
                      <td>{displayValue(row.pat_id)}</td>
                      <td>{row.page}</td>
                      <td>{row.operation}</td>
                      <td className="admin-errors-error-preview">{row.error.split('\n')[0]}</td>
                      <td className="admin-errors-actions">
                        <span className="admin-errors-expand-label">
                          {isExpanded ? 'Collapse' : 'Expand'}
                        </span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="admin-errors-detail-row">
                        <td colSpan={6}>
                          <div className="admin-errors-detail-panel">
                            <div className="admin-errors-detail-grid">
                              {detailFields.map((field) => (
                                <div
                                  key={`${rowId}-${field.label}`}
                                  className={`admin-errors-detail-item${field.multiline ? ' admin-errors-detail-item-multiline' : ''}`}
                                >
                                  <div className="admin-errors-detail-label">{field.label}</div>
                                  <div className="admin-errors-detail-value">
                                    {field.multiline ? (
                                      <pre className="admin-errors-detail-pre">{field.value}</pre>
                                    ) : (
                                      field.value
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div className="admin-errors-load-more">
          <button
            type="button"
            className="btn btn-login"
            disabled={loadingMore}
            onClick={() => loadErrors(offset + PAGE_SIZE, true, appliedFilters)}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminPhoneErrorsPage;
